// IGK-2P v2.1 Phase-3 — Sketch-stage CD panel.
// Mounts INSIDE SketchCard (behind REACT_APP_IGK2P_ENABLED). Renders:
//   • CD-approved badge (sketch)
//   • Cards strip: cast chip (face+scene-costume), corner+product, stale dots,
//     secondary cast = grey silhouette
//   • Blueprint three-state control (DELTA-1): icon → thumbnail → side-by-side
//     viewer (graphite | lineart) with element checklist + Approve / Redraw /
//     Edit-sketch-instead. Generating shows a live spinner + polls to ready/fail.
//   • Gated "Preview Keyframe" — enabled only when cards + blueprint + sketch
//     are all green; fires a REAL render and surfaces queued/failed state (never
//     a fake success).
import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle2, Circle, Loader2, ImageIcon, PenLine, RefreshCw, X, User, Shirt, MapPin, Package, AlertTriangle } from 'lucide-react';
import { toast } from '../../utils/toast';
import { blueprintState, STATE_META, genBlueprintShot, approveBlueprint, getBlueprints, regenIGK2P, unbindCast, iterateSketch } from './igk2pApi';
import SwapPicker from './SwapPicker';

const resolveUrl = (url) => (!url ? '' : url.startsWith('http') ? url : `${window.__API_BASE}${url}`);

function Chip({ icon: Icon, label, tone = 'normal', stale, silhouette, warn, onClick, onRemove, testid }) {
  const base = warn
    ? 'border-amber-400/50 text-amber-200 bg-amber-500/10'
    : silhouette
      ? 'border-dashed border-white/25 text-white/40 bg-white/5'
      : tone === 'ok'
        ? 'border-emerald-400/30 text-emerald-200 bg-emerald-500/10'
        : 'border-white/15 text-white/70 bg-white/5';
  return (
    <span data-testid={testid} onClick={onClick ? (e) => { e.stopPropagation(); onClick(); } : undefined}
      className={`relative inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md border text-[9px] ${base} ${onClick ? 'cursor-pointer hover:brightness-125' : ''}`}
      title={onClick ? 'Click to swap (Asset Bible)' : undefined}>
      <Icon className="w-2.5 h-2.5" />
      <span className="max-w-[80px] truncate">{label}</span>
      {warn && <AlertTriangle className="w-2.5 h-2.5" />}
      {onRemove && (
        <button type="button" data-testid={`${testid}-remove`}
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          title="Remove this cast tag from the shot"
          className="ml-0.5 -mr-0.5 rounded-full hover:bg-amber-400/30 p-0.5 leading-none">
          <X className="w-2.5 h-2.5" />
        </button>
      )}
    </span>
  );
}

export default function Igk2pSketchPanel({ shot, onReload, castTierMap }) {
  const [view, setView] = useState('icon'); // icon | thumb | viewer
  const [busy, setBusy] = useState(false);
  const [reSketchOpen, setReSketchOpen] = useState(false);
  const [reSketchText, setReSketchText] = useState('');
  const [reSketching, setReSketching] = useState(false);
  // Blueprint live state: generating spinner + poll → ready/failed (no manual refresh).
  const [bpGen, setBpGen] = useState(false);
  const [bpFail, setBpFail] = useState(null);   // failure reason string
  const [bpLive, setBpLive] = useState(null);   // {status, blueprint_url, ink_density}
  // Preview render state (real, honest): idle | queued | failed.
  const [preview, setPreview] = useState({ state: 'idle', reason: null });
  const [swap, setSwap] = useState(null);   // {kind, role} | null — swap picker
  const [unbind, setUnbind] = useState(null);   // {role} | null — unbind confirm popup
  const [lightbox, setLightbox] = useState(false); // preview-keyframe fullscreen

  const projectId = shot?.project_id;
  const rp = shot?.ref_plan || {};
  // 2026-07-15 — Resolved-cast source of truth = actual bindings, NOT ref_plan.
  // ref_plan is built lazily at keyframe time and is null on un-rendered shots,
  // which made every bound cast show a false "unresolved — pick card" badge.
  // A cast is resolved if it has a valid binding (cast_id set) OR is in the
  // (lazy) ref_plan. Name comparison is normalized (case + trailing "(...)").
  const _normCast = (s) => (s || '').toString().trim().toLowerCase().replace(/\s*\(.*?\)\s*/g, '').replace(/[^a-z0-9]+/g, ' ').trim();
  const _validBindings = ((shot?.asset_bible_spec?.cast_bindings) || []).filter((b) => b && b.cast_id);
  // Green chips: one label per binding (prefer card name), unioned with ref_plan.
  const _displayCast = [];
  const _seenCast = new Set();
  for (const c of [...(rp.cast_names || []), ..._validBindings.map((b) => b.cast_name || b.cast_role)].filter(Boolean)) {
    const k = _normCast(c);
    if (k && !_seenCast.has(k)) { _seenCast.add(k); _displayCast.push(c); }
  }
  // Resolution tokens: a visible cast is resolved if it matches a bound card's
  // NAME or its bound ROLE (handles name variance like "Decorated Ceremonial
  // Elephant" → card "Ceremonial elephants"), or appears in the lazy ref_plan.
  const _resolvedTokens = new Set([
    ...(rp.cast_names || []).map(_normCast),
    ..._validBindings.flatMap((b) => [b.cast_name, b.cast_role]).filter(Boolean).map(_normCast),
  ]);
  const isCastResolved = (c) => _resolvedTokens.has(_normCast(c));
  // 2026-07-15 — secondary/background cast get NO generated card by design
  // (cast_tier_map), so they must not raise a "pick card" badge. Only cast
  // NOT explicitly tagged 'secondary' are eligible for binding.
  const _tierOf = (c) => {
    const m = castTierMap || {};
    const key = Object.keys(m).find((k) => _normCast(k) === _normCast(c));
    return key ? (m[key] || '').toLowerCase() : '';
  };
  const _bindable = (c) => _tierOf(c) !== 'secondary';
  const _bindableVisible = (shot?.cast_visible || []).filter(_bindable);

  // Merge polled live blueprint fields over the (possibly stale) shot prop.
  const mergedShot = bpLive ? { ...shot, blueprint_status: bpLive.status, blueprint_url: bpLive.blueprint_url, blueprint_ink_density: bpLive.ink_density } : shot;
  const bpState = bpFail ? 'red' : blueprintState(mergedShot);
  const meta = STATE_META[bpState];
  const lineart = resolveUrl(mergedShot?.blueprint_url);
  const inkDensity = mergedShot?.blueprint_ink_density;

  const sketchReady = ['completed', 'accepted'].includes(shot?.sketch_status) || !!shot?.selected_sketch_angle;
  // 2026-07-14 — the "CD" chip reflects an EXPLICIT CD approval record only
  // (server truth: shot.blueprint_cd_approved, same field `blueprints-igk2p`
  // returns as `approved`). It must NEVER infer approval from sketch render
  // status (`sketch_status==='completed'`) — that was a fail-open display that
  // showed green "✓ CD" on imported projects with zero real approvals. Neutral
  // "CD — pending" until a genuine approval action writes the record.
  const cdApproval = mergedShot?.blueprint_cd_approved || shot?.blueprint_cd_approved || null;
  const cdApproved = !!cdApproval;
  const graphite = resolveUrl(shot?.sketch_s3_url || shot?.sketch_url);
  // Preview-keyframe look-ahead (quarantined field — never the standing keyframe,
  // never fed to video). Displayed here so the CD can actually SEE the render.
  const _rawPreviewUrl = shot?.keyframe_preview_url || '';
  // Defensive read-side guard: an ungraded intermediate (pass1/pass1_exp/etch)
  // must NEVER surface as a CD preview even if a stale row slips through.
  const _previewIsIntermediate = /_igk2p_pass1(_|\.)/.test(_rawPreviewUrl) && !/_igk2p_pass2/.test(_rawPreviewUrl);
  const previewKf = _previewIsIntermediate ? '' : resolveUrl(_rawPreviewUrl);
  const previewGenAt = shot?.keyframe_preview_generated_at;
  const previewFailed = shot?.keyframe_preview_status === 'failed' || _previewIsIntermediate;
  const previewFailReason = shot?.keyframe_preview_error || (_previewIsIntermediate ? 'ungraded intermediate — re-render preview' : '');
  const previewRendering = (['generating'].includes(shot?.keyframe_igk2p_status) || preview.state === 'rendering' || preview.state === 'queued') && !previewFailed;

  // Run-with-it (2026-07-16): mirror the backend gate — the blueprint must
  // physically EXIST (block only when missing / failed / still drawing), but it
  // no longer needs CD approval or freshness to preview. Unapproved / stale
  // blueprints render freely; cast bindings are never a preview lock.
  const bpStatus = mergedShot?.blueprint_status;
  const bpMissing = !lineart || bpStatus === 'failed' || bpStatus === 'not_generated' || !bpStatus;
  const bpGenerating = bpStatus === 'generating';
  const missing = [];
  if (bpMissing) missing.push('blueprint');
  else if (bpGenerating) missing.push('blueprint (still drawing)');
  if (!sketchReady) missing.push('sketch');
  const previewReady = missing.length === 0;

  const doUnbind = useCallback(async (scope) => {
    const role = unbind?.role;
    setUnbind(null);
    if (!role) return;
    try {
      const res = await unbindCast(shot.id, { role, scope });
      if (res?.updated > 0) toast.success(`Removed "${role}" from ${res.updated} shot${res.updated > 1 ? 's' : ''}`);
      else toast.info(`"${role}" was already untagged`);
      onReload?.();
    } catch (e) {
      toast.error(`Unbind failed: ${e?.response?.data?.detail || e.message}`);
    }
  }, [unbind, shot.id, onReload]);

  // ── Blueprint status polling (auto-swap to thumbnail when ready) ──────────
  const pollBlueprint = useCallback(async () => {
    if (!projectId) return;
    try {
      const data = await getBlueprints(projectId);
      const row = (data.shots || []).find((s) => s.shot_id === shot.id);
      if (!row) return;
      const status = row.status || (row.blueprint_url ? 'ready' : 'generating');
      if (status === 'ready' && row.blueprint_url) {
        setBpLive({ status: 'ready', blueprint_url: row.blueprint_url, ink_density: row.ink_density });
        setBpGen(false); setBpFail(null);
        onReload?.();
      } else if (status === 'failed' || row.state === 'failed') {
        setBpFail(row.reason || row.error || 'blueprint generation failed');
        setBpGen(false);
      }
    } catch { /* keep polling */ }
  }, [projectId, shot.id, onReload]);

  useEffect(() => {
    if (!bpGen) return undefined;
    const t = setInterval(pollBlueprint, 3000);
    const stop = setTimeout(() => { setBpGen(false); }, 120000); // safety cap
    return () => { clearInterval(t); clearTimeout(stop); };
  }, [bpGen, pollBlueprint]);

  // One-shot catch-up: when the CD opens a card whose shot prop predates the
  // blueprint (e.g. it finished in another session), fetch current status once
  // so a ready blueprint swaps in without a hard reload.
  useEffect(() => {
    if (view !== 'icon' && !lineart && !bpGen && !bpFail && !bpLive) {
      pollBlueprint();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view]);

  const doGenBlueprint = async () => {
    setBusy(true); setBpFail(null);
    try {
      await genBlueprintShot(shot.id);
      toast.success('Blueprint queued — generating lineart…');
      setBpGen(true);
    } catch (e) {
      setBpFail(e.response?.data?.detail || 'Blueprint gen failed');
      toast.error(e.response?.data?.detail || 'Blueprint gen failed');
    } finally { setBusy(false); }
  };

  const doApprove = async () => {
    setBusy(true);
    try {
      const r = await approveBlueprint(shot.id);
      if (r.success) { toast.success('Blueprint CD-approved'); onReload?.(); }
      else toast.error(`Cannot approve: ${r.reason}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Approve failed'); }
    finally { setBusy(false); }
  };

  const doReSketch = async () => {
    const feedback = reSketchText.trim();
    if (!feedback) { toast.message('Describe what to change, then re-sketch.'); return; }
    setReSketching(true);
    try {
      await iterateSketch(shot.id, feedback);
      toast.success('Re-sketch queued — updating the graphite…');
      setReSketchText('');
      setReSketchOpen(false);
      setTimeout(() => onReload?.(), 1200);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Re-sketch failed');
    } finally { setReSketching(false); }
  };

  const doPreview = async () => {
    if (!previewReady) return;
    setPreview({ state: 'rendering', reason: null });
    try {
      const r = await regenIGK2P(shot.id, { allow_missing_sketch: true, allow_stale_bindings: true, allow_empty_canon: true, preview_only: true });
      if (r.success) {
        setPreview({ state: 'queued', reason: null });
        toast.success('Preview render queued — the keyframe appears on this card when ready (~3–8 min).');
      } else {
        setPreview({ state: 'failed', reason: r.reason || r.message || 'render blocked' });
        toast.error(`Preview blocked: ${r.reason || r.message || 'render blocked'}`);
      }
    } catch (e) {
      const reason = e.response?.data?.detail || 'preview render failed';
      setPreview({ state: 'failed', reason });
      toast.error(`Preview failed: ${reason}`);
    }
  };

  // While a preview render is in flight, poll the project so the finished
  // keyframe surfaces on this card without a manual refresh.
  useEffect(() => {
    if (preview.state !== 'queued' && preview.state !== 'rendering') return;
    const baseline = shot?.keyframe_preview_generated_at || null;
    let ticks = 0;
    const iv = setInterval(async () => {
      ticks += 1;
      await onReload?.();
      if (ticks > 48) clearInterval(iv); // ~9.5 min safety stop
    }, 12000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview.state]);

  // When a fresh preview URL lands, clear the transient "queued" state.
  useEffect(() => {
    if ((preview.state === 'queued' || preview.state === 'rendering') && shot?.keyframe_igk2p_status && shot.keyframe_igk2p_status !== 'generating' && previewKf) {
      setPreview({ state: 'idle', reason: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shot?.keyframe_igk2p_status, previewKf]);

  return (
    <div className="mt-1.5 border-t border-white/5 pt-1.5" data-testid={`igk2p-sketch-panel-${shot.id}`}>
      {/* Row: CD badge + cards strip + blueprint icon */}
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[9px] font-semibold border ${cdApproved ? 'border-emerald-400/40 text-emerald-300 bg-emerald-500/10' : 'border-white/15 text-white/40 bg-white/5'}`}
          title={cdApproved
            ? `CD approved${cdApproval?.at ? ' · ' + new Date(cdApproval.at).toLocaleString(undefined, { timeZoneName: 'short' }) + ' (UTC: ' + cdApproval.at + ')' : ''}${cdApproval?.by ? ' · by ' + cdApproval.by : ''}`
            : 'CD approval pending — no approval record yet'}
          data-testid={`igk2p-sketch-cd-badge-${shot.id}`}>
          {cdApproved ? <CheckCircle2 className="w-2.5 h-2.5" /> : <Circle className="w-2.5 h-2.5" />} {cdApproved ? 'CD' : 'CD — pending'}
        </span>

        {_displayCast.map((c, i) => (
          <Chip key={`c${i}`} icon={User} label={c} tone="ok" stale={shot?.bindings_stale} onClick={() => setSwap({ kind: 'cast', role: c })} testid={`igk2p-cast-chip-${shot.id}-${i}`} />
        ))}
        {(rp.costume_names || []).slice(0, 1).map((c, i) => (
          <Chip key={`w${i}`} icon={Shirt} label={c} stale={shot?.bindings_stale} onClick={() => setSwap({ kind: 'costume', role: c })} testid={`igk2p-costume-chip-${shot.id}-${i}`} />
        ))}
        {rp.location_name && <Chip icon={MapPin} label={rp.location_name} stale={shot?.dop_spec_stale} onClick={() => setSwap({ kind: 'corner', role: rp.location_name })} testid={`igk2p-corner-chip-${shot.id}`} />}
        {rp.product_in_frame && <Chip icon={Package} label="Product" tone="ok" testid={`igk2p-product-chip-${shot.id}`} />}
        {_bindableVisible.filter((c) => !isCastResolved(c)).slice(0, 3).map((c, i) => (
          <Chip key={`s${i}`} icon={User} label={c} warn onClick={() => setSwap({ kind: 'cast', role: c })} onRemove={() => setUnbind({ role: c })} testid={`igk2p-secondary-chip-${shot.id}-${i}`} />
        ))}
        {(() => {
          const unresolved = _bindableVisible.filter((c) => !isCastResolved(c));
          const flags = shot?.binding_flags || [];
          if (unresolved.length === 0 && flags.length === 0) return null;
          const n = unresolved.length || flags.length;
          return (
            <button data-testid={`igk2p-unresolved-badge-${shot.id}`}
              onClick={(e) => { e.stopPropagation(); setSwap({ kind: 'cast', role: unresolved[0] || '' }); }}
              title={flags.join(' · ') || 'Parsed cast with no card — pick one'}
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[9px] font-semibold border border-amber-400/50 text-amber-200 bg-amber-500/15 hover:bg-amber-500/25">
              <AlertTriangle className="w-2.5 h-2.5" /> {n} cast unresolved — pick card
            </button>
          );
        })()}

        {/* Blueprint three-state trigger icon */}
        <button
          data-testid={`igk2p-blueprint-icon-${shot.id}`}
          onClick={(e) => { e.stopPropagation(); setView(view === 'icon' ? 'thumb' : view === 'thumb' ? 'viewer' : 'icon'); }}
          title={`Blueprint: ${meta.label} — click to ${view === 'icon' ? 'preview' : view === 'thumb' ? 'open viewer' : 'collapse'}`}
          className={`ml-auto inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-white/5 hover:bg-white/10 ring-1 ${meta.ring} transition-colors`}
        >
          {bpGen ? <Loader2 className={`w-2.5 h-2.5 animate-spin ${meta.text}`} /> : <span className={`w-2 h-2 rounded-full ${meta.dot}`} />}
          <span className={`text-[9px] font-semibold ${meta.text}`}>BP</span>
        </button>
      </div>

      {/* Thumbnail state */}
      {view === 'thumb' && (
        <div className="mt-1.5" data-testid={`igk2p-blueprint-thumb-${shot.id}`}>
          {bpGen ? (
            <div className="flex items-center gap-2 px-2 py-3 rounded-md bg-white/5 border border-white/10 animate-pulse" data-testid={`igk2p-blueprint-generating-${shot.id}`}>
              <Loader2 className="w-4 h-4 animate-spin text-cyan-300" />
              <span className="text-[10px] text-cyan-200">Generating lineart…</span>
            </div>
          ) : bpFail ? (
            <div className="flex items-center justify-between gap-2 px-2 py-2 rounded-md bg-red-500/10 border border-red-400/30" data-testid={`igk2p-blueprint-failed-${shot.id}`}>
              <span className="text-[9px] text-red-300 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Failed: {bpFail}</span>
              <button data-testid={`igk2p-blueprint-retry-${shot.id}`} disabled={busy} onClick={(e) => { e.stopPropagation(); doGenBlueprint(); }}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-amber-500/20 text-amber-200 text-[9px] hover:bg-amber-500/30 disabled:opacity-40">
                <RefreshCw className="w-3 h-3" /> Retry
              </button>
            </div>
          ) : lineart ? (
            <img src={lineart} alt="blueprint" className="w-full max-h-28 object-contain rounded-md bg-white border border-white/10" />
          ) : (
            <div className="flex items-center justify-between gap-2 px-2 py-2 rounded-md bg-white/5 border border-white/10">
              <span className="text-[10px] text-white/50">No blueprint yet.</span>
              <button data-testid={`igk2p-gen-blueprint-${shot.id}`} disabled={busy} onClick={(e) => { e.stopPropagation(); doGenBlueprint(); }}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-cyan-500/20 text-cyan-200 text-[9px] hover:bg-cyan-500/30 disabled:opacity-40">
                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <ImageIcon className="w-3 h-3" />} Generate lineart
              </button>
            </div>
          )}
          {!bpGen && !bpFail && inkDensity != null && (
            <div className="text-[8px] text-white/40 mt-0.5">ink density {(inkDensity * 100).toFixed(1)}%</div>
          )}
        </div>
      )}

      {/* Viewer state: side-by-side graphite | lineart + checklist + actions */}
      {view === 'viewer' && (
        <div className="mt-1.5 rounded-lg border border-white/10 bg-black/40 p-2" data-testid={`igk2p-blueprint-viewer-${shot.id}`}>
          <div className="flex items-center justify-between mb-1.5">
            <span className={`text-[9px] font-semibold ${meta.text}`}>Blueprint · {bpGen ? 'Generating…' : meta.label}</span>
            <button onClick={(e) => { e.stopPropagation(); setView('icon'); }} className="text-white/40 hover:text-white" data-testid={`igk2p-viewer-close-${shot.id}`}><X className="w-3 h-3" /></button>
          </div>
          <div className="grid grid-cols-2 gap-1.5">
            <figure>
              <img src={graphite || ''} alt="graphite" className="w-full aspect-video object-cover rounded-md border border-white/10" />
              <figcaption className="text-[8px] text-white/40 text-center mt-0.5">graphite (human ref)</figcaption>
            </figure>
            <figure>
              {bpGen ? <div className="w-full aspect-video rounded-md bg-white/5 border border-white/10 flex items-center justify-center gap-1 animate-pulse"><Loader2 className="w-4 h-4 animate-spin text-cyan-300" /><span className="text-[9px] text-cyan-200">Generating…</span></div>
                : lineart ? <img src={lineart} alt="lineart" className="w-full aspect-video object-contain rounded-md bg-white border border-white/10" />
                : <div className="w-full aspect-video rounded-md bg-white/5 border border-white/10 flex items-center justify-center text-[9px] text-white/40">no lineart</div>}
              <figcaption className="text-[8px] text-white/40 text-center mt-0.5">lineart (composition source)</figcaption>
            </figure>
          </div>

          <div className="mt-2">
            <div className="flex items-center gap-1.5">
              {bpState === 'red' ? (
                <button data-testid={`igk2p-redraw-blueprint-${shot.id}`} disabled={busy || bpGen} onClick={(e) => { e.stopPropagation(); doGenBlueprint(); }}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-cyan-500/20 text-cyan-200 text-[9px] hover:bg-cyan-500/30 disabled:opacity-40">
                  {bpGen ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Redraw blueprint
                </button>
              ) : (
                <button data-testid={`igk2p-approve-blueprint-${shot.id}`} disabled={busy || bpGen || bpState !== 'amber'} onClick={(e) => { e.stopPropagation(); doApprove(); }}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-emerald-500/20 text-emerald-200 text-[9px] hover:bg-emerald-500/30 disabled:opacity-30">
                  {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />} Approve
                </button>
              )}
              <button data-testid={`igk2p-resketch-toggle-${shot.id}`} disabled={reSketching} onClick={(e) => { e.stopPropagation(); setReSketchOpen((v) => !v); }}
                className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-amber-500/15 text-amber-200 text-[9px] hover:bg-amber-500/25 disabled:opacity-40">
                <PenLine className="w-3 h-3" /> Re-sketch
              </button>
            </div>
            {reSketchOpen && (
              <div className="mt-1.5" data-testid={`igk2p-resketch-box-${shot.id}`} onClick={(e) => e.stopPropagation()}>
                <textarea
                  autoFocus
                  value={reSketchText}
                  onChange={(e) => setReSketchText(e.target.value)}
                  placeholder="Describe the change — e.g. 'tighter framing on her face, lower the camera'"
                  rows={2}
                  className="w-full text-[10px] rounded-md bg-black/50 border border-white/15 text-white/80 px-2 py-1.5 outline-none focus:border-amber-400/50 resize-none"
                  data-testid={`igk2p-resketch-input-${shot.id}`}
                />
                <div className="flex items-center gap-1.5 mt-1">
                  <button data-testid={`igk2p-resketch-submit-${shot.id}`} disabled={reSketching || !reSketchText.trim()} onClick={(e) => { e.stopPropagation(); doReSketch(); }}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-amber-500/25 text-amber-100 text-[9px] font-semibold hover:bg-amber-500/40 disabled:opacity-40">
                    {reSketching ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Iterate sketch
                  </button>
                  <button data-testid={`igk2p-resketch-cancel-${shot.id}`} onClick={(e) => { e.stopPropagation(); setReSketchOpen(false); setReSketchText(''); }}
                    className="px-2 py-1 rounded-md bg-white/5 text-white/50 text-[9px] hover:text-white/80">Cancel</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Gated Preview-Keyframe — real render + honest state */}
      <div className="mt-1.5">
        <button
          data-testid={`igk2p-preview-keyframe-${shot.id}`}
          disabled={!previewReady || preview.state === 'rendering'}
          title={previewReady ? 'Blueprint ready — render a preview keyframe (not locked, not fed to video)' : `Locked: waiting on ${missing.join(', ')}`}
          onClick={(e) => { e.stopPropagation(); doPreview(); }}
          className={`w-full inline-flex items-center justify-center gap-1 px-2 py-1 rounded-md text-[9px] font-semibold transition-colors ${previewReady ? 'bg-gradient-to-r from-fuchsia-500/25 to-amber-500/25 text-white hover:from-fuchsia-500/40 hover:to-amber-500/40' : 'bg-white/5 text-white/35 cursor-not-allowed'} disabled:opacity-60`}
        >
          {preview.state === 'rendering' && <Loader2 className="w-3 h-3 animate-spin" />}
          {!previewReady ? `Preview locked — need: ${missing.join(' · ')}`
            : preview.state === 'rendering' ? 'Queuing preview…'
            : preview.state === 'queued' ? 'Preview queued ✓'
            : 'Preview Keyframe'}
        </button>
        {preview.state === 'queued' && (
          <div className="text-[8px] text-emerald-300/80 mt-0.5" data-testid={`igk2p-preview-queued-${shot.id}`}>Rendering (~3–8 min) — the keyframe appears below when ready. Not lockable, not fed to video.</div>
        )}
        {preview.state === 'failed' && (
          <div className="flex items-center justify-between gap-2 mt-0.5" data-testid={`igk2p-preview-failed-${shot.id}`}>
            <span className="text-[8px] text-red-300 flex items-center gap-1"><AlertTriangle className="w-2.5 h-2.5" /> {preview.reason}</span>
            <button onClick={(e) => { e.stopPropagation(); doPreview(); }} className="text-[8px] text-amber-200 hover:underline">Retry</button>
          </div>
        )}
      </div>

      {/* Rendered preview keyframe — the look-ahead result (quarantined field). */}
      {(previewKf || previewRendering || previewFailed) && (
        <div className="mt-1.5" data-testid={`igk2p-preview-result-${shot.id}`}>
          <div className="flex items-center justify-between mb-0.5">
            <span className="text-[8px] uppercase tracking-wide text-fuchsia-300/80 font-semibold">Preview keyframe · look-ahead</span>
            {previewGenAt && <span className="text-[8px] text-white/35">{new Date(previewGenAt).toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit' })} IST</span>}
          </div>
          {previewFailed ? (
            <div className="flex items-start justify-between gap-2 px-2 py-2 rounded-md bg-red-500/10 border border-red-400/30" data-testid={`igk2p-preview-failed-state-${shot.id}`}>
              <span className="text-[9px] text-red-300 flex items-start gap-1">
                <AlertTriangle className="w-3 h-3 mt-px flex-shrink-0" />
                <span>Preview didn't complete — {previewFailReason || 'render stopped mid-pipeline'}. No graded frame produced (nothing shown to avoid a misleading ungraded intermediate).</span>
              </span>
              <button onClick={(e) => { e.stopPropagation(); doPreview(); }} disabled={!previewReady}
                className="text-[9px] text-amber-200 hover:underline disabled:opacity-40 flex-shrink-0" data-testid={`igk2p-preview-retry-failed-${shot.id}`}>Retry</button>
            </div>
          ) : previewKf ? (
            <button type="button" onClick={(e) => { e.stopPropagation(); setLightbox(true); }}
              className="block w-full group relative rounded-md overflow-hidden border border-fuchsia-400/20 hover:border-fuchsia-400/50 transition-colors"
              title="Click to view full size" data-testid={`igk2p-preview-thumb-${shot.id}`}>
              <img src={previewKf} alt="preview keyframe" loading="lazy" decoding="async" className="w-full max-h-40 object-cover" />
              <span className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                <span className="opacity-0 group-hover:opacity-100 text-[9px] text-white bg-black/60 px-2 py-0.5 rounded-full transition-opacity">Enlarge</span>
              </span>
            </button>
          ) : (
            <div className="flex items-center gap-2 px-2 py-4 rounded-md bg-fuchsia-500/5 border border-fuchsia-400/15 animate-pulse" data-testid={`igk2p-preview-rendering-${shot.id}`}>
              <Loader2 className="w-4 h-4 animate-spin text-fuchsia-300" />
              <span className="text-[10px] text-fuchsia-200/80">Rendering preview keyframe…</span>
            </div>
          )}
          {previewKf && (
            <button type="button" disabled={preview.state === 'rendering' || previewRendering}
              onClick={(e) => { e.stopPropagation(); doPreview(); }}
              className="mt-1 inline-flex items-center gap-1 text-[8px] text-white/50 hover:text-fuchsia-200 disabled:opacity-40"
              data-testid={`igk2p-preview-regen-${shot.id}`}>
              <RefreshCw className="w-2.5 h-2.5" /> Re-render preview
            </button>
          )}
        </div>
      )}

      {lightbox && previewKf && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/85 p-6" onClick={(e) => { e.stopPropagation(); setLightbox(false); }} data-testid={`igk2p-preview-lightbox-${shot.id}`}>
          <button onClick={(e) => { e.stopPropagation(); setLightbox(false); }} className="absolute top-4 right-4 text-white/70 hover:text-white" data-testid={`igk2p-preview-lightbox-close-${shot.id}`}><X className="w-6 h-6" /></button>
          <figure className="max-w-[90vw] max-h-[90vh] flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
            <img src={previewKf} alt="preview keyframe full" className="max-w-full max-h-[82vh] object-contain rounded-lg" />
            <figcaption className="mt-2 text-[11px] text-white/50">Preview keyframe (look-ahead) · {shot?.scene_number}-{shot?.shot_number} · not locked, not fed to video</figcaption>
          </figure>
        </div>,
        document.body
      )}
      {swap && <SwapPicker shot={shot} kind={swap.kind} role={swap.role} onClose={() => setSwap(null)} onReload={onReload} />}
      {unbind && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60" onClick={(e) => { e.stopPropagation(); setUnbind(null); }} data-testid={`igk2p-unbind-popup-${shot.id}`}>
          <div className="w-[300px] rounded-xl border border-amber-400/30 bg-neutral-900 p-4 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="text-sm font-semibold text-white">Remove tag · {unbind.role}</div>
            <p className="mt-1 text-[11px] text-white/60">Unbind this cast tag so it stops requesting a card. Apply to:</p>
            <div className="mt-3 flex flex-col gap-2">
              <button data-testid={`igk2p-unbind-shot-${shot.id}`} onClick={() => doUnbind('shot')}
                className="w-full px-3 py-2 rounded-md text-xs font-medium bg-white/10 text-white hover:bg-white/20">This shot only</button>
              <button data-testid={`igk2p-unbind-scene-${shot.id}`} onClick={() => doUnbind('scene')}
                className="w-full px-3 py-2 rounded-md text-xs font-medium bg-amber-500/20 text-amber-200 border border-amber-400/30 hover:bg-amber-500/30">Whole scene ({shot?.scene_number})</button>
              <button data-testid={`igk2p-unbind-cancel-${shot.id}`} onClick={() => setUnbind(null)}
                className="w-full px-3 py-1.5 rounded-md text-[11px] text-white/50 hover:text-white/80">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
